import React, { useState, useEffect } from 'react'
import axios from 'axios'
import { toast } from 'react-hot-toast'
import { Route, Phone, AlertTriangle, Calendar, BookOpen, Plus, Trash2, Play, Save, Settings, FileText } from 'lucide-react'
import { pollJob } from '../lib/jobs'
import ProductGuide from '../components/ProductGuide'
import AgentAsk from '../components/AgentAsk'

// The same intake engine serves any vertical — only the 6 specialty labels
// change. These presets map to the Company A/B/C example (law/medical/accounting).
const PRESETS = {
  'Law Firm': [
    { id: 'real_estate', name: 'Real Estate', description: 'Land transactions, conveyancing, leases, property disputes' },
    { id: 'corporate', name: 'Corporate', description: 'Business formation, contracts, M&A, governance' },
    { id: 'hr_employment', name: 'HR & Employment', description: 'Workplace disputes, wrongful termination, compliance' },
    { id: 'family_law', name: 'Family Law', description: 'Divorce, custody, wills, estates, probate' },
    { id: 'insolvency', name: 'Insolvency', description: 'Bankruptcy, restructuring, debt recovery' },
    { id: 'specialist', name: 'Specialist', description: 'Niche, uncommon, or undefined practice areas' },
  ],
  'Medical Group': [
    { id: 'cardiology', name: 'Cardiology', description: 'Heart conditions, chest pain, arrhythmia, blood pressure' },
    { id: 'orthopedics', name: 'Orthopedics', description: 'Bones, joints, fractures, sports injuries, back pain' },
    { id: 'pediatrics', name: 'Pediatrics', description: 'Child and infant health, vaccinations, development' },
    { id: 'neurology', name: 'Neurology', description: 'Brain, nerves, headaches, seizures, numbness' },
    { id: 'oncology', name: 'Oncology', description: 'Cancer screening, diagnosis, treatment' },
    { id: 'general_practice', name: 'General Practice', description: 'General checkups, common illness, referrals' },
  ],
  'Accounting': [
    { id: 'tax_prep', name: 'Tax Preparation', description: 'Personal and business tax filing, deductions, planning' },
    { id: 'audit', name: 'Audit', description: 'Financial statement audits, assurance, compliance' },
    { id: 'payroll', name: 'Payroll', description: 'Payroll processing, withholdings, filings' },
    { id: 'estate_planning', name: 'Estate Planning', description: 'Wills, trusts, inheritance, succession' },
    { id: 'forensic', name: 'Forensic Accounting', description: 'Fraud investigation, litigation support, disputes' },
    { id: 'consulting', name: 'Consulting', description: 'Advisory, valuation, strategy, general finance' },
  ],
}
const DEFAULT_SPECIALTIES = PRESETS['Law Firm']

export default function IntakeManager() {
  const [instances, setInstances] = useState([])
  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState({
    instanceName: '',
    firmName: '',
    specialties: DEFAULT_SPECIALTIES.map(s => ({...s})),
    greeting: 'Thank you for calling. How can I direct your call today?'
  })
  const [selectedInstance, setSelectedInstance] = useState(null)
  const [activeTab, setActiveTab] = useState('instances')
  const [intakes, setIntakes] = useState([])
  const [testTranscript, setTestTranscript] = useState('')
  const [testResult, setTestResult] = useState(null)
  const [knowledge, setKnowledge] = useState([])
  const [settingsForm, setSettingsForm] = useState(null)
  const [savingSettings, setSavingSettings] = useState(false)

  // Open an instance: load its intakes, knowledge state, and seed the Settings form.
  const openInstance = (inst, tab = 'test') => {
    setSelectedInstance(inst)
    setActiveTab(tab)
    loadIntakes(inst.id)
    loadKnowledge(inst.id)
    setSettingsForm({
      instanceName: inst.instance_name || '',
      greeting: inst.config?.greeting || 'Thank you for calling. How can I direct your call today?',
      firmName: inst.config?.firmName || '',
      escalationTriggers: (inst.config?.escalationTriggers || ['urgent', 'emergency', 'domestic violence', "I'm scared"]).join(', '),
      enableConflictCheck: inst.config?.enableConflictCheck !== false,
      enableCalendarBooking: inst.config?.enableCalendarBooking !== false,
      specialties: (inst.specialties || DEFAULT_SPECIALTIES).map(s => ({ ...s }))
    })
  }

  const loadKnowledge = async (instId) => {
    try {
      const res = await axios.get(`/api/v1/intake/instances/${instId}/knowledge`)
      setKnowledge(res.data.knowledge || [])
    } catch { setKnowledge([]) }
  }

  const setSpec = (i, field, value) => setSettingsForm(f => {
    const specialties = [...f.specialties]; specialties[i] = { ...specialties[i], [field]: value }; return { ...f, specialties }
  })
  const addSpec = () => setSettingsForm(f => ({ ...f, specialties: [...f.specialties, { id: `desk_${Date.now()}`, name: '', description: '' }] }))
  const removeSpec = (i) => setSettingsForm(f => ({ ...f, specialties: f.specialties.filter((_, idx) => idx !== i) }))

  const saveIntakeSettings = async () => {
    if (!selectedInstance || !settingsForm) return
    const specialties = settingsForm.specialties
      .map(s => ({ ...s, id: (s.id || s.name || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '_'), name: s.name.trim() }))
      .filter(s => s.id && s.name)
    if (specialties.length < 1) { toast.error('Add at least one specialty desk'); return }
    setSavingSettings(true)
    try {
      const res = await axios.put(`/api/v1/intake/instances/${selectedInstance.id}`, {
        instanceName: settingsForm.instanceName,
        specialties,
        config: {
          greeting: settingsForm.greeting,
          firmName: settingsForm.firmName,
          escalationTriggers: settingsForm.escalationTriggers.split(',').map(s => s.trim()).filter(Boolean),
          enableConflictCheck: settingsForm.enableConflictCheck,
          enableCalendarBooking: settingsForm.enableCalendarBooking
        }
      })
      toast.success('Settings saved')
      const updated = res.data.instance
      setInstances(prev => prev.map(x => x.id === updated.id ? { ...x, ...updated } : x))
      setSelectedInstance(prev => ({ ...prev, ...updated }))
      loadKnowledge(selectedInstance.id)
    } catch (err) {
      toast.error(err.response?.data?.error || 'Save failed')
    } finally { setSavingSettings(false) }
  }

  useEffect(() => {
    loadInstances()
  }, [])

  const loadInstances = async () => {
    try {
      const res = await axios.get('/api/v1/intake/instances')
      setInstances(res.data.instances || [])
    } catch {
      setInstances([])
    }
  }

  const createInstance = async (e) => {
    e.preventDefault()
    try {
      const res = await axios.post('/api/v1/intake/instances', {
        instanceName: form.instanceName,
        specialties: form.specialties,
        config: { firmName: form.firmName, greeting: form.greeting }
      })
      toast.success('Law firm intake system created!')
      setInstances([...instances, res.data.instance])
      setShowForm(false)
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create')
    }
  }

  const updateSpecialty = (index, field, value) => {
    const updated = [...form.specialties]
    updated[index] = { ...updated[index], [field]: value }
    setForm({ ...form, specialties: updated })
  }

  const [kbInputs, setKbInputs] = useState({})

  const runTestIntake = async () => {
    if (!testTranscript.trim() || !selectedInstance) return
    toast.loading('Processing intake...')
    try {
      const res = await axios.post(`/api/v1/intake/instances/${selectedInstance.id}/intake`, {
        callerPhone: '+1-555-0100',
        callerId: 'caller_test',
        transcript: testTranscript
      })
      toast.dismiss()
      setTestResult(res.data)
      loadIntakes(selectedInstance.id)
    } catch (err) {
      toast.dismiss()
      toast.error(err.response?.data?.error || 'Intake processing failed')
    }
  }

  const uploadKnowledge = async (specialtyId) => {
    const content = (kbInputs[specialtyId] || '').trim()
    if (!content || !selectedInstance) return
    toast.loading('Queuing ingestion...')
    try {
      const res = await axios.post(`/api/v1/intake/instances/${selectedInstance.id}/knowledge/${specialtyId}`, { content })
      const done = res.data.jobId ? await pollJob(res.data.jobId) : null
      toast.dismiss()
      toast.success(done ? `Ingested ${done.chunks ?? ''} chunk(s)` : 'Knowledge ingested')
      setKbInputs(prev => ({ ...prev, [specialtyId]: '' }))
      loadKnowledge(selectedInstance.id)
    } catch (err) {
      toast.dismiss()
      toast.error(err.response?.data?.error || 'Knowledge upload failed')
    }
  }

  const loadIntakes = async (instId) => {
    try {
      const res = await axios.get(`/api/v1/intake/instances/${instId}/intakes`)
      setIntakes(res.data.intakes || [])
    } catch {
      setIntakes([])
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Multi-Specialty Intake</h2>
          <p className="text-gray-500 mt-1">Vertical-agnostic AI intake — law, medical, accounting & more</p>
        </div>
        <button onClick={() => setShowForm(true)} className="btn-primary flex items-center gap-2">
          <Plus size={18} /> New Intake System
        </button>
      </div>

      <ProductGuide product="intake" collapsible defaultOpen={false} />

      <div className="max-w-2xl"><AgentAsk service="intake" agentName="Intake Swarm" starters={['How many callers were routed?', 'Any escalations I should see?']} /></div>

      {showForm && (
        <div className="card max-w-3xl">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Configure Intake System</h3>
          <form onSubmit={createInstance} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Instance Name</label>
                <input type="text" className="input" required placeholder="Smith &amp; Associates Intake"
                  value={form.instanceName} onChange={e => setForm({...form, instanceName: e.target.value})} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Firm Name</label>
                <input type="text" className="input" required placeholder="Smith &amp; Associates"
                  value={form.firmName} onChange={e => setForm({...form, firmName: e.target.value})} />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Reception Greeting</label>
              <input type="text" className="input" value={form.greeting}
                onChange={e => setForm({...form, greeting: e.target.value})} />
            </div>
            <div>
              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">Load a vertical preset</label>
                <div className="flex gap-2">
                  {Object.keys(PRESETS).map(key => (
                    <button key={key} type="button"
                      onClick={() => setForm({ ...form, specialties: PRESETS[key].map(x => ({ ...x })) })}
                      className="px-3 py-1.5 rounded-lg text-sm font-medium bg-gray-100 text-gray-700 hover:bg-primary-600 hover:text-white transition-colors">
                      {key}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-gray-400 mt-1">Same engine, different labels — customize any specialty below.</p>
              </div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Specialties (Configure all 6)</label>
              <div className="space-y-3">
                {form.specialties.map((spec, i) => (
                  <div key={spec.id} className="flex gap-3 items-start p-3 bg-gray-50 rounded-lg">
                    <div className="w-8 h-8 bg-primary-500/15 text-primary-600 rounded-full flex items-center justify-center text-sm font-bold shrink-0">
                      {i + 1}
                    </div>
                    <div className="flex-1 space-y-2">
                      <input type="text" className="input text-sm" value={spec.name}
                        onChange={e => updateSpecialty(i, 'name', e.target.value)} />
                      <textarea className="input text-sm h-16" value={spec.description}
                        onChange={e => updateSpecialty(i, 'description', e.target.value)} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex gap-2">
              <button type="submit" className="btn-primary flex items-center gap-2">
                <Save size={16} /> Create Intake System
              </button>
              <button type="button" onClick={() => setShowForm(false)} className="btn-secondary">Cancel</button>
            </div>
          </form>
        </div>
      )}

      {/* Instances */}
      <div className="grid grid-cols-3 gap-6">
        {instances.map(inst => (
          <div key={inst.id}
            onClick={() => openInstance(inst, 'test')}
            className={`card cursor-pointer transition-all ${selectedInstance?.id === inst.id ? 'ring-2 ring-primary-500' : 'hover:shadow-md'}`}>
            <div className="flex items-center justify-between mb-3">
              <Route className="text-primary-600" size={28} />
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${inst.status === 'active' ? 'bg-green-500/15 text-green-600' : 'bg-gray-100 text-gray-600'}`}>
                {inst.status}
              </span>
            </div>
            <h3 className="text-lg font-semibold text-gray-900">{inst.instance_name}</h3>
            <p className="text-xs text-gray-500 mt-1">{inst.specialties?.length || 6} specialties configured</p>
            <div className="mt-3 flex flex-wrap gap-1">
              {(inst.specialties || DEFAULT_SPECIALTIES).map((s, i) => (
                <span key={i} className="px-2 py-0.5 bg-primary-500/10 text-primary-600 text-xs rounded-full">{s.name}</span>
              ))}
            </div>
            <p className="text-xs mt-3 font-medium text-primary-600">Click to route a caller, view records &amp; edit desks →</p>
          </div>
        ))}
      </div>

      {/* Manage Instance */}
      {selectedInstance && activeTab !== 'instances' && (
        <div className="space-y-6">
          <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit">
            {['test', 'knowledge', 'intakes', 'settings'].map(tab => (
              <button key={tab} onClick={() => { setActiveTab(tab); if (tab === 'intakes') loadIntakes(selectedInstance.id); if (tab === 'knowledge') loadKnowledge(selectedInstance.id) }}
                className={`px-4 py-2 rounded-md text-sm font-medium capitalize transition-colors ${activeTab === tab ? 'bg-gray-100 text-gray-900 shadow-sm' : 'text-gray-600'}`}>
                {tab}
              </button>
            ))}
          </div>

          {activeTab === 'test' && (
            <div className="card max-w-2xl">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Phone size={20} /> Test Intake Call
              </h3>
              <textarea className="input h-32 mb-4" placeholder="Enter caller transcript... e.g. 'I have a court hearing tomorrow for my divorce and I need a lawyer immediately'"
                value={testTranscript} onChange={e => setTestTranscript(e.target.value)} />
              <button onClick={runTestIntake} className="btn-primary flex items-center gap-2">
                <Play size={16} /> Process Intake
              </button>

              {testResult && (
                <div className="mt-6 p-4 bg-gray-50 rounded-lg space-y-3">
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      testResult.type === 'escalation' ? 'bg-red-500/15 text-red-600' :
                      testResult.type === 'conflict' ? 'bg-orange-500/15 text-orange-600' :
                      'bg-green-500/15 text-green-600'
                    }`}>{testResult.type}</span>
                  </div>
                  {testResult.specialty && <p><span className="font-medium">Routed to:</span> {testResult.specialty}</p>}
                  {testResult.summary && <p><span className="font-medium">Summary:</span> {testResult.summary}</p>}
                  {testResult.knowledgeAnswer && <p><span className="font-medium">Knowledge answer:</span> {testResult.knowledgeAnswer}</p>}
                  {testResult.message && testResult.type !== 'intake_complete' && <p><span className="font-medium">Response:</span> {testResult.message}</p>}
                  {testResult.appointment && (
                    <p className="flex items-center gap-2">
                      <Calendar size={16} className="text-primary-500" />
                      <span>Appointment: {testResult.appointment.date} at {testResult.appointment.time}</span>
                    </p>
                  )}
                  {testResult.escalation && (
                    <p className="flex items-center gap-2 text-red-600">
                      <AlertTriangle size={16} />
                      <span>Escalated to human staff</span>
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {activeTab === 'knowledge' && (
            <div className="card">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <BookOpen size={20} /> Specialty Knowledge Bases
                </h3>
                <button onClick={() => loadKnowledge(selectedInstance.id)} className="text-sm text-gray-500 hover:text-gray-900">Refresh</button>
              </div>
              <p className="text-sm text-gray-500 mb-4">Each desk answers from its own knowledge. Paste content to add to a desk — the counts show what's already indexed.</p>
              <div className="space-y-4">
                {(selectedInstance.specialties || DEFAULT_SPECIALTIES).map((spec, i) => {
                  const k = knowledge.find(x => x.specialtyId === spec.id)
                  const indexed = k && k.chunkCount > 0
                  return (
                    <div key={spec.id} className="p-4 border border-gray-200 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium text-gray-900">{spec.name}</h4>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${indexed ? 'bg-green-500/15 text-green-600' : 'bg-gray-100 text-gray-500'}`}>
                            {indexed ? `${k.chunkCount} passage${k.chunkCount === 1 ? '' : 's'} indexed` : 'empty'}
                          </span>
                        </div>
                        <span className="text-xs text-gray-400">ID: {spec.id}</span>
                      </div>
                      <textarea className="input h-24 text-sm mb-2" placeholder={`Paste ${spec.name} knowledge base content...`}
                        value={kbInputs[spec.id] || ''}
                        onChange={e => setKbInputs(prev => ({ ...prev, [spec.id]: e.target.value }))} />
                      <button onClick={() => uploadKnowledge(spec.id)} className="btn-secondary text-sm flex items-center gap-1">
                        <Save size={14} /> Add to Knowledge Base
                      </button>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {activeTab === 'intakes' && (
            <div className="card">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Intakes</h3>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-2 text-gray-600">Caller</th>
                    <th className="text-left py-2 text-gray-600">Specialty</th>
                    <th className="text-left py-2 text-gray-600">Status</th>
                    <th className="text-left py-2 text-gray-600">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {intakes.length === 0 && (
                    <tr><td colSpan="4" className="py-8 text-center text-gray-400">No intakes yet. Run a test to see results here.</td></tr>
                  )}
                  {intakes.map(row => (
                    <tr key={row.id} className="border-b border-gray-100 align-top">
                      <td className="py-2 text-gray-800 whitespace-nowrap">{row.caller_phone || row.caller_id || '—'}</td>
                      <td className="py-2 text-gray-800">
                        {row.specialty_routed || '—'}
                        {row.intake_summary && <p className="text-xs text-gray-500 mt-0.5 max-w-md">{row.intake_summary}</p>}
                      </td>
                      <td className="py-2">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          row.status === 'escalated' ? 'bg-red-500/15 text-red-600' :
                          row.status === 'open' ? 'bg-green-500/15 text-green-600' : 'bg-gray-100 text-gray-600'
                        }`}>{row.status}</span>
                        {row.conflict_status && row.conflict_status !== 'clear' && (
                          <span className="ml-1 px-2 py-0.5 rounded-full text-xs font-medium bg-orange-500/15 text-orange-600">{row.conflict_status}</span>
                        )}
                      </td>
                      <td className="py-2 text-gray-500 whitespace-nowrap">{new Date(row.created_at).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === 'settings' && settingsForm && (
            <div className="card max-w-2xl space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-1 flex items-center gap-2">
                  <Settings size={20} /> Intake settings
                </h3>
                <p className="text-sm text-gray-500">Everything here changes how the receptionist behaves — greeting, when it escalates to a human, and the specialty desks it routes to.</p>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Instance name</label>
                  <input className="input" value={settingsForm.instanceName} onChange={e => setSettingsForm(f => ({ ...f, instanceName: e.target.value }))} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Business / firm name</label>
                  <input className="input" placeholder="REDNA Health" value={settingsForm.firmName} onChange={e => setSettingsForm(f => ({ ...f, firmName: e.target.value }))} />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Reception greeting</label>
                <input className="input" value={settingsForm.greeting} onChange={e => setSettingsForm(f => ({ ...f, greeting: e.target.value }))} />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Escalate to a human when the caller says… (comma-separated)</label>
                <textarea className="input h-20 text-sm" value={settingsForm.escalationTriggers} onChange={e => setSettingsForm(f => ({ ...f, escalationTriggers: e.target.value }))} />
                <p className="text-xs text-gray-400 mt-1">Any of these phrases hands the caller straight to a person — never left to the bot.</p>
              </div>

              <div className="flex flex-wrap gap-4">
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" checked={settingsForm.enableConflictCheck} onChange={e => setSettingsForm(f => ({ ...f, enableConflictCheck: e.target.checked }))} />
                  Run a conflict check
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" checked={settingsForm.enableCalendarBooking} onChange={e => setSettingsForm(f => ({ ...f, enableCalendarBooking: e.target.checked }))} />
                  Offer appointment booking
                </label>
              </div>

              <div className="border-t border-gray-200 pt-5">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">Specialty desks ({settingsForm.specialties.length})</label>
                  <button onClick={addSpec} className="btn-secondary text-sm flex items-center gap-1"><Plus size={14} /> Add desk</button>
                </div>
                <div className="space-y-3">
                  {settingsForm.specialties.map((spec, i) => (
                    <div key={i} className="flex gap-3 items-start p-3 bg-gray-50 rounded-lg">
                      <div className="flex-1 space-y-2">
                        <input className="input text-sm" placeholder="Desk name (e.g. Cancer Screening)" value={spec.name} onChange={e => setSpec(i, 'name', e.target.value)} />
                        <textarea className="input text-sm h-16" placeholder="What this desk covers — used to route callers" value={spec.description || ''} onChange={e => setSpec(i, 'description', e.target.value)} />
                      </div>
                      <button onClick={() => removeSpec(i)} disabled={settingsForm.specialties.length <= 1}
                        className="p-2 text-gray-400 hover:text-red-600 disabled:opacity-30" title="Remove desk">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <button onClick={saveIntakeSettings} disabled={savingSettings} className="btn-primary disabled:opacity-60">
                {savingSettings ? 'Saving…' : 'Save settings'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
